import { motion } from "framer-motion";
import { CircleCheck, MapPin, ShieldCheck, Wrench } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const POINTS = [
  "17+ years of medical imaging expertise",
  "Pan-India support network",
  "End-to-end imaging ecosystem",
  "Equipment, service & spare parts supply",
  "Helium & cryogenic support",
  "Long-term maintenance & lifecycle care",
];

const pills = [
  { icon: ShieldCheck, label: "Expertise", value: "17+ Years", pos: "pill-top", accent: "bg-medis-blue/[0.08] text-medis-blue" },
  { icon: MapPin, label: "Coverage", value: "Pan-India", pos: "pill-right", accent: "bg-medis-teal/[0.08] text-medis-teal" },
  { icon: Wrench, label: "Services", value: "Equipment + Support + Helium", pos: "pill-bottom", accent: "bg-medis-blue/[0.08] text-medis-blue" },
];

export default function About() {
  return (
    <section id="about" className="py-24 lg:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-12 lg:gap-16 lg:items-center">
          <div className="order-1 lg:order-1 lg:col-span-5 relative mt-6">
            <Reveal>
              <div className="relative rounded-xl border border-medis-silver/30 overflow-hidden">
                <motion.img
                  alt="Doctor analyzing medical scans"
                  initial={{ scale: 1.05 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                  src="/about.jpg"
                />
              </div>
            </Reveal>
            {pills.map((p, i) => (
              <div
                key={p.label}
                className={`${p.pos} absolute z-10 flex items-center gap-2 md:gap-3 bg-white rounded-xl border border-medis-silver/30 shadow-lg px-3 py-2 md:px-4 md:py-3 cursor-default hover:shadow-xl hover:scale-105 transition-[shadow,transform] duration-200 ${
                  p.pos === "pill-top"
                    ? "-top-4 left-2 md:-top-5 md:-left-5"
                    : p.pos === "pill-right"
                    ? "top-1/2 -right-2 md:-right-6"
                    : "-bottom-4 left-2 md:-bottom-5 md:-left-5"
                }`}
                style={{ animation: `float-pill ${5 + i}s ease-in-out ${i * 0.8}s infinite` }}
              >
                <div className={`h-8 w-8 md:h-9 md:w-9 rounded-lg flex items-center justify-center shrink-0 ${p.accent}`}>
                  <p.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[9px] md:text-[9.5px] uppercase tracking-[0.16em] text-medis-gray font-semibold">
                    {p.label}
                  </div>
                  <div className="text-[12px] md:text-[13.5px] font-bold text-medis-navy leading-tight whitespace-nowrap">
                    {p.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="order-2 lg:order-2 lg:col-span-7">
            <Reveal>
              <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">
                About MEDIS Group LLP
              </div>
              <h2 className="mt-3 font-display font-bold text-medis-navy text-3xl sm:text-4xl lg:text-[30px] leading-[1.1] tracking-tight">
                A complete medical imaging ecosystem built for hospitals that demand reliability
              </h2>
              <div className="mt-6 space-y-5 text-medis-gray text-[15.5px] leading-relaxed max-w-2xl">
                <p>
                  MEDIS Group LLP is more than an equipment supplier. We are a complete medical imaging ecosystem built
                  to help hospitals and diagnostic centres source, deploy, maintain and scale imaging infrastructure
                  with confidence.
                </p>
                <p>
                  From equipment supply and technical support to spare parts and cryogenic services, we help keep
                  imaging operations reliable and efficient across the full lifecycle of MRI, CT and Cath Lab
                  installations.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
                {POINTS.map((point, i) => (
                  <div key={point} className="flex items-start gap-2.5" data-testid={`about-point-${i}`}>
                    <CircleCheck className="h-4 w-4 text-medis-green shrink-0 mt-0.5" />
                    <span className="text-[13.5px] text-medis-navy font-medium leading-snug">{point}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
